import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Plus, Search } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ListContainer } from "@/components/common/ListItem";
import { UnreadBadgeStack } from "@/components/common/UnreadBadgeStack";
import { Button } from "@/components/ui/button";
import { ChatRoomList } from "@/features/chat/components/ChatRoomSectionList";
import { ChatSearchModal } from "@/features/chat/components/ChatSearchModal";
import { CreateChatModal } from "@/features/chat/components/CreateChatModal";
import { buildUnreadBadgeItems } from "@/features/chat/utils/unreadBadges";
import { useChatRoomInfo } from "@/hooks/useChatRoomInfo";
import { useChatRoomsSection } from "@/hooks/useChatRoomsSection";
import { useChatSectionState } from "@/hooks/useChatSectionState";
import { chatsApi } from "@/lib/humhub-api/chats";
import type { ChatRoomType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface ChatListProps {
  containerHeight?: string | number;
  containerClassName?: string;
  onClick?: () => void;
  showActions?: boolean;
  isExpanded?: boolean;
  onHeaderToggle?: () => void;
  showTabs?: boolean;
}

export interface ChatListRef {
  refetch: () => Promise<void>;
}

type ChatSectionKey = "messages" | "topics" | "invites";

const CHAT_SECTION_STORAGE_KEY = "chatList.sectionState";
const CHAT_SECTION_KEYS: ChatSectionKey[] = ["messages", "topics", "invites"];
const DEFAULT_SECTION_KEY: ChatSectionKey = "messages";
const CHAT_SECTION_BY_TYPE: Record<ChatRoomType, ChatSectionKey> = {
  user_dm: "messages",
  user_group: "topics",
  system: "invites",
};

// ローディング状態コンポーネント
const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-4 text-center text-variable-collection-dark-gray">
    {message}
  </div>
);

// エラー状態コンポーネント
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="py-4 text-center text-variable-collection-red">{error}</div>
);

// 空状態コンポーネント
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-4 text-center text-variable-collection-dark-gray">
    {message}
  </div>
);

export const ChatList = forwardRef<ChatListRef, ChatListProps>(
  (
    {
      containerHeight = "450px",
      containerClassName,
      onClick,
      showActions = true,
      isExpanded = true,
      onHeaderToggle,
      showTabs = true,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [hasManualSectionOverride, setHasManualSectionOverride] =
      useState(false);
    const PER_PAGE = 50;
    const { state: sectionState, setState: setSectionState } =
      useChatSectionState(
        CHAT_SECTION_STORAGE_KEY,
        DEFAULT_SECTION_KEY,
        CHAT_SECTION_KEYS,
      );
    const { data: allUnreadCounts } = useQuery({
      queryKey: ["chatRoomsUnreadTotals", "all"],
      queryFn: () => chatsApi.getChatUnreadCounts(),
      staleTime: 1 * 60 * 1000,
    });

    const messagesSection = useChatRoomsSection("user_dm", PER_PAGE, true);
    const topicsSection = useChatRoomsSection("user_group", PER_PAGE, true);
    const invitesSection = useChatRoomsSection("system", PER_PAGE, true);

    // データをリフレッシュする関数
    const handleRefresh = useCallback(async () => {
      await Promise.all([
        messagesSection.refresh(),
        topicsSection.refresh(),
        invitesSection.refresh(),
      ]);
    }, [messagesSection, topicsSection, invitesSection]);

    const handleChatItemClick = (roomId: number | undefined) => {
      if (onClick) {
        onClick();
      }
      navigate({ to: `/chat-room/${roomId}` });
    };

    const getCurrentChatRoomId = () => {
      const match = pathname.match(/\/chat-room\/(\d+)/);
      return match ? match[1] : null;
    };

    const currentChatRoomId = getCurrentChatRoomId();
    const { data: currentChatRoom } = useChatRoomInfo(currentChatRoomId ?? "");

    useEffect(() => {
      if (!currentChatRoomId) return;
      setHasManualSectionOverride(false);
    }, [currentChatRoomId]);

    useEffect(() => {
      if (hasManualSectionOverride) return;
      if (!currentChatRoom?.type) return;
      const nextSection = CHAT_SECTION_BY_TYPE[currentChatRoom.type];
      if (nextSection && nextSection !== sectionState) {
        setSectionState(nextSection);
      }
    }, [
      currentChatRoom?.type,
      hasManualSectionOverride,
      sectionState,
      setSectionState,
    ]);

    // チャット作成時の処理 - 直接リフレッシュを呼び出す
    const handleChatCreated = useCallback(
      async (sectionKey: "messages" | "topics") => {
        setSectionState(sectionKey);
        await handleRefresh();
      },
      [handleRefresh, setSectionState],
    );

    // 外部からrefetchを呼び出せるようにする
    useImperativeHandle(
      ref,
      () => ({
        refetch: handleRefresh,
      }),
      [handleRefresh],
    );

    const isInitialLoading =
      messagesSection.isLoading ||
      topicsSection.isLoading ||
      invitesSection.isLoading;
    const error =
      messagesSection.error || topicsSection.error || invitesSection.error;

    const sections = [
      {
        key: "messages" as const,
        title: t("chat.sectionMessages"),
        section: messagesSection,
        showParticipantsOnly: true,
      },
      {
        key: "topics" as const,
        title: t("chat.sectionTopics"),
        section: topicsSection,
        showParticipantsOnly: false,
      },
      {
        key: "invites" as const,
        title: t("chat.sectionInvites"),
        section: invitesSection,
        showParticipantsOnly: false,
      },
    ];

    const selectedSectionKey = sectionState;
    const selectedSection =
      sections.find((section) => section.key === selectedSectionKey) ??
      sections[0];

    const handleSectionToggle = useCallback(
      (key: ChatSectionKey) => {
        setHasManualSectionOverride(true);
        setSectionState(key);
      },
      [setSectionState],
    );

    const renderContent = () => {
      if (!isExpanded) {
        return null;
      }

      const selectedRoomsCount =
        selectedSection.section.accumulatedRooms.length;

      if (isInitialLoading && selectedRoomsCount === 0) {
        return (
          <div className="flex flex-1 items-center justify-center">
            <LoadingState message={t("common.loading")} />
          </div>
        );
      }

      if (error) {
        return (
          <div className="flex flex-1 items-center justify-center">
            <ErrorState error={error} />
          </div>
        );
      }

      if (selectedRoomsCount === 0) {
        return (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState message={t("chat.noChatRoomsFound")} />
          </div>
        );
      }

      return (
        <ChatRoomList
          rooms={selectedSection.section.accumulatedRooms}
          onRoomClick={handleChatItemClick}
          currentChatRoomId={currentChatRoomId}
          hasMore={selectedSection.section.hasMore}
          onLoadMore={selectedSection.section.loadMore}
          isLoadingMore={selectedSection.section.isLoadingMore}
          showParticipantsOnly={selectedSection.showParticipantsOnly}
        />
      );
    };

    const actionButtons = (
      <div className="flex items-center space-x-1">
        {showActions ? (
          <>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                setIsSearchModalOpen(true);
              }}
              variant="ghost"
              size="icon"
              className="size-6 rounded-lg p-0.5"
            >
              <Search />
            </Button>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                setIsCreateModalOpen(true);
              }}
              variant="ghost"
              size="icon"
              className="size-6 rounded-lg bg-custom-color-lightTeal p-0.5 text-white"
            >
              <Plus />
            </Button>
          </>
        ) : null}
        {onHeaderToggle ? (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              onHeaderToggle();
            }}
            variant="ghost"
            size="icon"
            className="size-6 rounded-lg p-0.5"
            aria-label={isExpanded ? "Collapse section" : "Expand section"}
          >
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </Button>
        ) : null}
      </div>
    );

    const headerRowClassName = cn(
      "flex h-[34px] w-full items-center justify-between px-1.5 text-left",
      !isExpanded &&
        "-ml-1.5 h-[34px] min-h-[34px] w-[calc(var(--sidebar-width)-var(--sidebar-padding-left)-var(--sidebar-padding-right))] rounded-[0px_30px_30px_0px] bg-custom-white pl-4 pr-1.5 shadow-[0px_4px_15px_rgba(0,0,0,0.1)]",
    );
    const headerTitle = (
      <h2 className="inline-flex items-center gap-2 text-xl font-semibold text-text-subtitle">
        <span>{t("chat.chats")}</span>
        <UnreadBadgeStack
          items={buildUnreadBadgeItems(
            t,
            allUnreadCounts?.unreadMessages ?? 0,
            allUnreadCounts?.unreadMentions ?? 0,
          )}
          align="start"
          display="inline-flex"
        />
      </h2>
    );
    const headerRow = onHeaderToggle ? (
      <div className={headerRowClassName}>
        <button
          type="button"
          onClick={onHeaderToggle}
          className="flex min-w-0 flex-1 items-center text-left"
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
          aria-expanded={isExpanded}
        >
          {headerTitle}
        </button>
        {actionButtons}
      </div>
    ) : (
      <div className={headerRowClassName}>
        {headerTitle}
        {actionButtons}
      </div>
    );

    const header = (
      <div className="flex flex-col">
        {headerRow}
        <hr
          className={cn(
            "mb-1 rounded-full border-t-2 border-variable-collection-light-gray",
            !isExpanded && "hidden",
          )}
        />
        {showTabs ? (
          <div className="bg-custom-white px-1.5 py-0.5">
            <div className="flex h-5 items-center gap-0 text-xs">
              {sections.map((section, index) => {
                const isActive = section.key === selectedSectionKey;
                return (
                  <span
                    key={section.key}
                    className="inline-flex flex-1 items-center"
                  >
                    <button
                      type="button"
                      className={cn(
                        "inline-flex w-full flex-nowrap items-center justify-center gap-1 text-muted-foreground transition-colors",
                        isActive && "text-text-title",
                      )}
                      onClick={() => handleSectionToggle(section.key)}
                      aria-pressed={isActive}
                    >
                      <span
                        className={cn(
                          "min-w-0 whitespace-nowrap",
                          isActive && "underline underline-offset-4",
                        )}
                      >
                        {section.title}
                      </span>
                      {section.section.totalUnreadCounts ? (
                        <UnreadBadgeStack
                          items={buildUnreadBadgeItems(
                            t,
                            section.section.totalUnreadCounts.unreadMessages ??
                              0,
                            section.section.totalUnreadCounts.unreadMentions ??
                              0,
                          )}
                          align="start"
                          direction="row"
                          display="inline-flex"
                          className="gap-0 whitespace-nowrap"
                        />
                      ) : null}
                    </button>
                    {index < sections.length - 1 ? (
                      <span
                        aria-hidden="true"
                        className="h-3 w-px bg-variable-collection-light-gray"
                      />
                    ) : null}
                  </span>
                );
              })}
            </div>
            <hr className="mt-1 rounded-full border-t-2 border-variable-collection-light-gray" />
          </div>
        ) : null}
      </div>
    );

    return (
      <>
        <ListContainer
          header={header}
          containerHeight={containerHeight}
          className={cn(
            isExpanded
              ? "overflow-x-hidden"
              : "overflow-x-visible overflow-y-visible",
            containerClassName,
            !isExpanded && "rounded-none bg-transparent p-0",
          )}
          dividerClassName="hidden"
          scrollClassName={
            isExpanded ? "overflow-y-auto [scrollbar-gutter:stable]" : "hidden"
          }
        >
          {renderContent()}
        </ListContainer>

        <CreateChatModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onChatCreated={handleChatCreated}
        />

        <ChatSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onChatRoomSelect={handleChatItemClick}
        />
      </>
    );
  },
);
